/**
 * components/client/FieldRenderer.tsx
 *
 * Renders the appropriate input UI for each field type in the client form.
 * Extracted from ClientCollect for modularity.
 *
 * Updated Jun 2026: Replaced native <input type="date"> with custom DatePickerField
 *   (3-select day/month/year) for correct rendering on iOS.
 *   Text/textarea inputs now call scrollIntoView on focus to keep the field
 *   visible above the mobile keyboard.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import {
  Upload, X, CheckCircle2, AlertCircle, RefreshCw,
  FileText, FilePlus, Camera,
} from 'lucide-react';
import Spinner from '../ui/Spinner';
import DatePickerField from './DatePickerField';
import type { RequestField } from '../../utils/previewHelpers';
import type { UploadedFileEntry } from '../../hooks/useClientForm';
import { safeParseOptions } from '../../utils/jsonHelpers';

const MAX_FILES = 10;

interface FieldRendererProps {
  field: RequestField;
  response: string;
  uploadedFiles: UploadedFileEntry[];
  onResponseChange: (value: string) => void;
  onFilesSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onFileRetry: (index: number) => void;
  onAutoAdvance?: () => void;
}

function getFileIcon(file: File) {
  if (file.type.startsWith('image/')) return null;
  return <FileText className="w-6 h-6 text-neutral-400" />;
}

function FileThumbnail({ entry }: { entry: UploadedFileEntry }) {
  const isImage = entry.file.type.startsWith('image/');
  const [objectUrl] = useState(() =>
    isImage ? URL.createObjectURL(entry.file) : null
  );

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  if (isImage && objectUrl) {
    return (
      <img
        src={objectUrl}
        alt={entry.file.name}
        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
      {getFileIcon(entry.file) ?? <FileText className="w-5 h-5 text-neutral-400" />}
    </div>
  );
}

interface FileListItemProps {
  entry: UploadedFileEntry;
  index: number;
  onRemove: (index: number) => void;
  onRetry: (index: number) => void;
}

function FileListItem({ entry, index, onRemove, onRetry }: FileListItemProps) {
  const sizeLabel = entry.file.size < 1024 * 1024
    ? `${(entry.file.size / 1024).toFixed(0)} Ko`
    : `${(entry.file.size / 1024 / 1024).toFixed(2)} Mo`;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-200 ${
        entry.error
          ? 'bg-error-50 border-error-200'
          : entry.uploaded
          ? 'bg-success-50 border-success-200'
          : 'bg-primary-50 border-primary-200'
      }`}
    >
      <FileThumbnail entry={entry} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-800 truncate">{entry.file.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-neutral-400">{sizeLabel}</span>
          {entry.uploading && (
            <span className="text-xs text-primary-600 flex items-center gap-1">
              <Spinner size="xs" />
              Envoi en cours...
            </span>
          )}
          {entry.uploaded && !entry.uploading && (
            <span className="text-xs text-success-600 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Envoyé
            </span>
          )}
          {entry.error && (
            <span className="text-xs text-error-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {entry.error}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        {entry.error && (
          <button
            type="button"
            onClick={() => onRetry(index)}
            className="p-1.5 rounded-lg text-primary-600 hover:bg-primary-100 transition-colors"
            title="Réessayer"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onRemove(index)}
          disabled={entry.uploading}
          className="p-1.5 rounded-lg text-neutral-400 hover:text-error-600 hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Retirer ce fichier"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

interface DocumentDropZoneProps {
  fieldId: string;
  uploadedFiles: UploadedFileEntry[];
  onFilesSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onFileRetry: (index: number) => void;
}

function DocumentDropZone({
  uploadedFiles,
  onFilesSelect,
  onFileRemove,
  onFileRetry,
}: DocumentDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileCount = uploadedFiles.length;
  const canAddMore = fileCount < MAX_FILES;

  const handleFiles = useCallback((files: File[]) => {
    if (files.length > 0) onFilesSelect(files);
  }, [onFilesSelect]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMore) setIsDragging(true);
  }, [canAddMore]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!canAddMore) return;
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [canAddMore, handleFiles]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
    if (inputRef.current) inputRef.current.value = '';
  }, [handleFiles]);

  return (
    <div className="space-y-3">
      {fileCount === 0 && (
        <div className="flex items-start gap-2.5 p-3 bg-neutral-50 rounded-xl border border-neutral-200">
          <Camera className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-neutral-500 leading-relaxed">
            Conseil : placez le document à plat sur un fond uni, photographiez-le bien en face (vue du dessus) et sans flash pour un résultat net.
          </p>
        </div>
      )}

      {fileCount > 0 && (
        <div className="space-y-2">
          {uploadedFiles.map((entry, i) => (
            <FileListItem
              key={i}
              entry={entry}
              index={i}
              onRemove={onFileRemove}
              onRetry={onFileRetry}
            />
          ))}
        </div>
      )}

      {canAddMore && (
        <label
          className={`block cursor-pointer`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={handleInputChange}
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.webp"
          />
          <div
            className={`border-2 border-dashed rounded-xl transition-all duration-200 ${
              fileCount > 0 ? 'py-4 px-5' : 'p-12'
            } ${
              isDragging
                ? 'border-primary-500 bg-primary-50 scale-[1.01]'
                : fileCount > 0
                ? 'border-neutral-200 hover:border-primary-400 hover:bg-neutral-50'
                : 'border-neutral-200 hover:border-primary-400 hover:bg-primary-50'
            }`}
          >
            {fileCount === 0 ? (
              <div className="text-center pointer-events-none">
                <div className={`flex justify-center mb-3 transition-transform duration-200 ${isDragging ? 'scale-110' : ''}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors duration-200 ${isDragging ? 'bg-primary-100' : 'bg-neutral-100'}`}>
                    <Upload className={`w-7 h-7 transition-colors duration-200 ${isDragging ? 'text-primary-600' : 'text-neutral-400'}`} />
                  </div>
                </div>
                <p className={`text-base font-medium mb-1 transition-colors duration-200 ${isDragging ? 'text-primary-700' : 'text-neutral-700'}`}>
                  {isDragging ? 'Déposez vos fichiers ici' : 'Glissez-déposez ou cliquez pour choisir'}
                </p>
                <p className="text-sm text-neutral-400">
                  PDF, DOC, DOCX, JPG, PNG, HEIC - max 10 Mo par fichier
                </p>
                {MAX_FILES > 1 && (
                  <p className="text-xs text-neutral-400 mt-1">
                    Jusqu'à {MAX_FILES} fichiers par champ
                  </p>
                )}
              </div>
            ) : (
              <div className={`flex items-center gap-3 pointer-events-none ${isDragging ? 'opacity-100' : ''}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${isDragging ? 'bg-primary-100' : 'bg-neutral-100'}`}>
                  <FilePlus className={`w-4 h-4 transition-colors duration-200 ${isDragging ? 'text-primary-600' : 'text-neutral-400'}`} />
                </div>
                <div>
                  <p className={`text-sm font-medium transition-colors duration-200 ${isDragging ? 'text-primary-700' : 'text-neutral-600'}`}>
                    {isDragging ? 'Déposez vos fichiers ici' : 'Ajouter un fichier'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </label>
      )}

      {!canAddMore && (
        <p className="text-xs text-neutral-400 text-center py-1">
          Limite de {MAX_FILES} fichiers atteinte
        </p>
      )}
    </div>
  );
}

export default function FieldRenderer({
  field,
  response,
  uploadedFiles,
  onResponseChange,
  onFilesSelect,
  onFileRemove,
  onFileRetry,
  onAutoAdvance,
}: FieldRendererProps) {
  switch (field.type) {
    case 'document':
      return (
        <DocumentDropZone
          fieldId={field.id}
          uploadedFiles={uploadedFiles}
          onFilesSelect={onFilesSelect}
          onFileRemove={onFileRemove}
          onFileRetry={onFileRetry}
        />
      );

    case 'short_text':
      if (field.is_system) {
        return (
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={response}
            onChange={(e) => onResponseChange(e.target.value)}
            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)}
            placeholder="exemple@domaine.fr"
            className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base transition-all duration-150 outline-none"
            autoFocus
          />
        );
      }
      return (
        <input
          type="text"
          value={response}
          onChange={(e) => onResponseChange(e.target.value)}
          onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)}
          placeholder="Votre réponse..."
          className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base transition-all duration-150 outline-none"
          autoFocus
        />
      );

    case 'text':
      return (
        <textarea
          value={response}
          onChange={(e) => onResponseChange(e.target.value)}
          onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)}
          placeholder="Votre réponse..."
          rows={4}
          className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-base transition-all duration-150 outline-none"
          autoFocus
        />
      );

    case 'single_choice':
      return (
        <div className="space-y-2.5">
          {safeParseOptions(field.options).map((option, index) => (
            <label
              key={index}
              className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-150 hover:scale-[1.01] ${
                response === option
                  ? 'border-primary-500 bg-primary-50 shadow-soft'
                  : 'border-neutral-200 hover:border-primary-300 hover:bg-neutral-50'
              }`}
            >
              <input
                type="radio"
                name={field.id}
                value={option}
                checked={response === option}
                onChange={(e) => {
                  onResponseChange(e.target.value);
                  if (onAutoAdvance) {
                    setTimeout(onAutoAdvance, 420);
                  }
                }}
                className="w-5 h-5 text-primary-600 focus:ring-primary-500 accent-primary-600"
              />
              <span className={`text-base ${response === option ? 'font-medium text-primary-900' : 'text-neutral-800'}`}>
                {option}
              </span>
            </label>
          ))}
        </div>
      );

    case 'multiple_choice': {
      const currentValues = response ? response.split(',') : [];
      return (
        <div className="space-y-2.5">
          {safeParseOptions(field.options).map((option, index) => {
            const isChecked = currentValues.includes(option);
            return (
              <label
                key={index}
                className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-150 hover:scale-[1.01] ${
                  isChecked
                    ? 'border-primary-500 bg-primary-50 shadow-soft'
                    : 'border-neutral-200 hover:border-primary-300 hover:bg-neutral-50'
                }`}
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={isChecked}
                  onChange={() => {
                    const newValues = isChecked
                      ? currentValues.filter((v) => v !== option)
                      : [...currentValues, option];
                    onResponseChange(newValues.join(','));
                  }}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 accent-primary-600"
                />
                <span className={`text-base ${isChecked ? 'font-medium text-primary-900' : 'text-neutral-800'}`}>
                  {option}
                </span>
              </label>
            );
          })}
        </div>
      );
    }

    case 'date':
      return (
        <DatePickerField value={response} onChange={onResponseChange} />
      );

    case 'info':
      return null;

    default:
      return null;
  }
}
